<script lang="ts" setup>
import { uniqBy } from "lodash-es";
import {
  callOnce,
  computed,
  useCreators,
  useMaps,
  usePlaces,
  watch,
} from "#imports";
import MPImg from "../widgets/MPImg.vue";
import MPBookmark from "../widgets/MPBookmark.vue";

const props = defineProps<{
  mapId: string;
  to?: string;
  actions?: boolean;
}>();

const emits = defineEmits<{
  updated: [];
}>();

const mapStore = useMaps();
const creatorStore = useCreators();
const placeStore = usePlaces();

const map = computed(() => {
  const m = mapStore.getById(props.mapId).value!;
  const creator = creatorStore.getById(m?.user_id).value!;
  const places = placeStore.getByIds(m.placeIds).value;

  return {
    ...m,
    places,
    creator,
  };
});

const loadData = async () => {
  const map = await mapStore.fetchById(props.mapId);
  await creatorStore.fetchById(map.value!.user_id);
  await placeStore.fetchByIds(map.value!.placeIds);
};

// we have to make the key unique to the component because otherwise it won't run more than once
await callOnce(`MPMapCard-${props.mapId}`, loadData, { mode: "navigation" });
watch(() => props.mapId, loadData);

const mergedCountries = computed(() => {
  if (!map.value?.places) {
    return [];
  }
  return uniqBy(map.value.places, "country");
});

const mergedPlaces = computed(() => {
  if (!map.value?.places) {
    return [];
  }
  return uniqBy(map.value.places, "place");
});
</script>

<template>
  <article class="map-card">
    <div class="image">
      <NuxtLinkLocale :to="to ? to : `/maps/${map.current_slug}`">
        <MPImg
          class="cover"
          :src="map.cover_image_url || undefined"
          :width="400"
          :height="450"
        />
      </NuxtLinkLocale>
      <div>
        <MPImg
          class="profile"
          :src="
            map.creator.profile_image_url ??
            '/media/images/placeholder-user.webp'
          "
          :width="100"
          :height="100"
        />
      </div>
    </div>
    <div class="actions-container" v-if="actions">
      <div class="actions">
        <MPBookmark
          size="small"
          version="secondary"
          link
          bookmark="map_bookmarks"
          :id="map.id"
          tooltip
          backgroundColor="var(--color-slate-100)"
          iconColor="var(--color-rose-500)"
          @completed="emits('updated')"
        />
      </div>
    </div>
    <div class="overlay">
      <div class="spots-country typeface-small">
        <NuxtLinkLocale
          v-if="
            map.spotIds.length > 0 &&
            mergedCountries.length > 0 &&
            mergedPlaces.length > 0
          "
          :to="to ? to : `/maps/${map.current_slug}`"
        >
          {{ mergedCountries[0]!.country }}&nbsp;-&nbsp;
          {{ mergedPlaces[0]!.placeRegion }}
          <span v-if="mergedPlaces.length > 1">
            <small class="typeface-detail"
              >&nbsp; +{{ mergedPlaces.length - 1 }} destination{{
                mergedPlaces.length > 2 ? "s" : ""
              }}</small
            >
          </span>
        </NuxtLinkLocale>
      </div>
      <h4 v-if="map.title" class="title typeface-summary">
        <NuxtLinkLocale :to="to ? to : `/maps/${map.current_slug}`">
          {{ map.title }}
        </NuxtLinkLocale>
      </h4>
      <div v-if="map.is_published" class="spots">
        <i class="ri-map-pin-fill" />
        <small> {{ map.spots_count }} {{ $t("explore.spots") }} </small>
      </div>
      <p class="summary typeface-regular">
        {{ map.summary }}
      </p>
      <small class="author">
        Made by
        <NuxtLinkLocale :to="`/@${map.creator.mappr_handle}`"
          >@{{ map.creator.mappr_handle }}</NuxtLinkLocale
        >
      </small>
      <NuxtLinkLocale
        v-if="map.categories.length > 0"
        :to="to ? to : `/maps/${map.current_slug}`"
        class="spots-category"
      >
        <span v-for="(category, idx) in map.categories.slice(0, 2)" :key="idx">
          <small
            v-if="category && category.name.length > 0"
            :title="category.name"
          >
            <i :class="`${category.icon}`" />&nbsp;{{ category.name }}
          </small>
        </span>
        <span v-if="map.categories.length >= 2">
          <small :title="`+ ${map.categories.length - 2} categories`">
            +{{ map.categories.length - 2 }} more
          </small>
        </span>
      </NuxtLinkLocale>
    </div>
  </article>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
.map-card {
  position: relative;
  display: grid;
  width: 100%;
  width: fit-container;
}

.title {
  color: var(--color-slate-900);
  font-weight: 500;
  margin: 0 0.5rem 0;
  line-height: 1.4;
  text-wrap: auto;
  @mixin screen md {
    margin: 0.5rem 0.5rem 0;
  }
  a {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.summary {
  color: var(--color-slate-500);
  font-size: 0.85rem;
  line-height: 1.4 !important;
  margin: 0.5rem 0.5rem 0.25rem 0.5rem;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  @mixin screen md {
    margin: 0.75rem 0.5rem 0.75rem 0.5rem;
  }
}

.author {
  display: flex;
  margin: 0.25rem 0.5rem;
  gap: 0.2rem;
  @mixin screen md {
    margin: 0.5rem;
  }
  a {
    font-weight: 500;
    color: var(--color-rose-500);
  }
}

.actions {
  display: grid;
  gap: 0.5rem;
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;

  &:deep(button label) {
    display: none;
  }
  &:deep(button i) {
    font-size: 1.5rem;
  }
}

.spots {
  position: absolute;
  top: 0.8rem;
  right: 0.35rem;
  display: inline-flex;
  margin: 0 0.45rem 0;
  gap: 0.25rem;
  color: var(--color-slate-500);
  font-weight: 500;
  background: var(--color-slate-50);
  padding: 0.25rem 0.3rem;
  border-radius: 0.45rem;
  box-shadow: var(--shadow-sm);
  i {
    color: var(--color-rose-400);
  }
}

a {
  display: flex;
}

.image {
  position: relative;
  height: fit-content;
}

.cover {
  border-radius: 1rem;
  aspect-ratio: 1/1;
  object-fit: cover;
  min-width: 100%;
}

.profile {
  position: absolute;
  left: 1rem;
  bottom: 1rem;
  width: 3rem;
  height: 3rem;
  object-fit: cover;
  border-radius: 100%;
  overflow: hidden;
  box-shadow: var(--shadow-base);
  border: 2px solid var(--color-slate-200);
}

.spots-country {
  display: inline-flex;
  margin: 1rem 0.5rem 0.25rem;
  width: 100%;
  color: var(--color-indigo-400);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0.5rem;
  small {
    color: var(--color-slate-400);
  }
}

.spots-country:nth-child(1) {
  padding-left: 0;
}

.spots-category {
  padding: 0.75rem 0.5rem 0.5rem;
  white-space: nowrap;
  display: inline-flex;
  gap: 0.5rem;
  width: fit-content;
  cursor: pointer;
  small {
    display: block;
    max-width: 10ch;
    width: fit-content;
    text-overflow: ellipsis;
    overflow: hidden;
    background-color: var(--color-slate-100);
    padding: 0.45rem 0.75rem;
    border-radius: 5rem;
    color: var(--color-slate-900);
    cursor: default;
    i {
      color: var(--color-slate-400);
    }
  }
}
</style>
